import { ComponentType, createContext, Dispatch, MutableRefObject } from "react";

import { WindowPosition } from "../../components/window/windowPositioning";

/**
 * Props for all window components (core and third-party).
 * WindowManager provides onClose and position.
 * Focus and z-index stacking are handled by WindowManager's wrapper div —
 * components do not need to accept or forward those concerns.
 */
export interface BaseWindowProps {
    position?: WindowPosition;
    onClose?: () => void;
}

export interface WindowConfig {
    id: string;
    defaultPosition: WindowPosition;
    component: ComponentType<BaseWindowProps>;
}

export interface WindowState {
    open: boolean;
    position: WindowPosition;
    zIndex: number;
    minimized: boolean;
    /** When true, the window renders in DockManager instead of WindowManager. */
    docked: boolean;
    /** Ascending top-to-bottom order in the dock. Meaningless when `docked` is false. */
    dockOrder: number;
    /** Pinned slot height. `undefined` → participates in equal-flex. Cleared on structural ops. */
    dockedHeight?: number;
    /** Floating position before docking; restored on undock. */
    floatingPositionSnapshot?: WindowPosition;
}

export interface WindowEntry {
    config: WindowConfig;
    state: WindowState;
}

export const initialState = {} as Record<string, WindowEntry>;

export type State = typeof initialState;

export enum ActionTypes {
    AddWindow,
    RemoveWindow,
    OpenWindow,
    CloseWindow,
    ToggleWindow,
    FocusWindow,
    ToggleMinimized,
    DockWindow,
    UndockWindow,
    SetDockedHeight,
    ReorderDockedWindows,
}

function addWindow(config: WindowConfig) {
    return {
        type: ActionTypes.AddWindow as const,
        config,
    };
}

function removeWindow(windowId: string) {
    return {
        type: ActionTypes.RemoveWindow as const,
        windowId,
    };
}

function openWindow(windowId: string, position?: WindowPosition, options?: { dock?: boolean }) {
    return {
        type: ActionTypes.OpenWindow as const,
        windowId,
        position,
        dock: options?.dock,
    };
}

function closeWindow(windowId: string) {
    return {
        type: ActionTypes.CloseWindow as const,
        windowId,
    };
}

function toggleWindow(windowId: string, position?: WindowPosition, options?: { dock?: boolean }) {
    return {
        type: ActionTypes.ToggleWindow as const,
        windowId,
        position,
        dock: options?.dock,
    };
}

function focusWindow(windowId: string) {
    return {
        type: ActionTypes.FocusWindow as const,
        windowId,
    };
}

function toggleMinimizedWindow(windowId: string) {
    return {
        type: ActionTypes.ToggleMinimized as const,
        windowId,
    };
}

function dockWindow(windowId: string, atIndex?: number) {
    return {
        type: ActionTypes.DockWindow as const,
        windowId,
        atIndex,
    };
}

function undockWindow(windowId: string, position?: WindowPosition) {
    return {
        type: ActionTypes.UndockWindow as const,
        windowId,
        position,
    };
}

function setDockedHeight(windowId: string, height: number) {
    return {
        type: ActionTypes.SetDockedHeight as const,
        windowId,
        height,
    };
}

function reorderDockedWindows(orderedIds: string[]) {
    return {
        type: ActionTypes.ReorderDockedWindows as const,
        orderedIds,
    };
}

export const actions = {
    addWindow,
    removeWindow,
    openWindow,
    closeWindow,
    toggleWindow,
    focusWindow,
    toggleMinimizedWindow,
    dockWindow,
    undockWindow,
    setDockedHeight,
    reorderDockedWindows,
};

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchWindows = Dispatch<Actions>;
export type LazyState = MutableRefObject<State>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchWindows>(undefined as any);

/**
 * Helper to renormalize Z-indices.
 * 1. Takes all currently open windows.
 * 2. Sorts them by their current zIndex.
 * 3. Moves the `activeWindowId` to the end of the list.
 * 4. Reassigns zIndex starting from baseZIndex (e.g. 1000).
 */
function reorderWindows(state: State, activeWindowId: string): State {
    const baseZIndex = 1000;

    const entries = Object.entries(state);
    const otherWindows = entries.filter(([id]) => id !== activeWindowId);
    const activeWindowEntry = entries.find(([id]) => id === activeWindowId);

    if (!activeWindowEntry) {
        return state;
    }

    otherWindows.sort(([, a], [, b]) => (a.state.zIndex || 0) - (b.state.zIndex || 0));
    const sortedEntries = [...otherWindows, activeWindowEntry];

    const nextState = { ...state };
    sortedEntries.forEach(([id, entry], index) => {
        nextState[id] = {
            ...entry,
            state: {
                ...entry.state,
                zIndex: baseZIndex + index,
            },
        };
    });

    return nextState;
}

/** Applies `newOrder` as the dock layout (each id becomes docked at its index with
 *  `dockedHeight` cleared — structural ops reset to equal-flex). Others pass through. */
function withRecomputedDockOrder(state: State, newOrder: string[]): State {
    const orderById = new Map<string, number>();
    newOrder.forEach((id, index) => orderById.set(id, index));

    const nextState: State = {};
    for (const [id, entry] of Object.entries(state)) {
        const dockOrder = orderById.get(id);
        if (dockOrder !== undefined) {
            nextState[id] = {
                ...entry,
                state: {
                    ...entry.state,
                    docked: true,
                    dockOrder,
                    dockedHeight: undefined,
                },
            };
        } else {
            nextState[id] = entry;
        }
    }
    return nextState;
}

function getDockedIdsInOrder(state: State, excludeId?: string): string[] {
    return Object.entries(state)
        .filter(([id, entry]) => entry.state.docked && id !== excludeId)
        .sort(([, a], [, b]) => a.state.dockOrder - b.state.dockOrder)
        .map(([id]) => id);
}

/**
 * Atomic "open into dock" transition (no floating→docked flicker). Appends to
 * the dock, snapshots the floating position, and resets sibling pinned heights.
 */
function openIntoDock(state: State, windowId: string, position: WindowPosition): State {
    const newOrder = [...getDockedIdsInOrder(state, windowId), windowId];

    const reordered = withRecomputedDockOrder(state, newOrder);
    const target = reordered[windowId];
    reordered[windowId] = {
        ...target,
        state: {
            ...target.state,
            open: true,
            position,
            zIndex: target.state.zIndex || 1000,
            minimized: false,
            floatingPositionSnapshot: position,
        },
    };
    return reorderWindows(reordered, windowId);
}

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.AddWindow: {
            const windowId = action.config.id;

            if (state[windowId]) {
                return state;
            }

            const maxZ = Math.max(1000, ...Object.values(state).map((w) => w.state.zIndex || 1000));

            return {
                ...state,
                [windowId]: {
                    config: action.config,
                    state: {
                        open: false,
                        position: action.config.defaultPosition,
                        zIndex: maxZ + 1,
                        minimized: false,
                        docked: false,
                        dockOrder: 0,
                    },
                },
            };
        }
        case ActionTypes.RemoveWindow: {
            const { [action.windowId]: _removed, ...remaining } = state;
            return remaining;
        }
        case ActionTypes.OpenWindow: {
            const windowEntry = state[action.windowId];

            if (!windowEntry) {
                console.warn(`Window "${action.windowId}" is not registered`);
                return state;
            }

            const resolvedPosition = action.position ?? windowEntry.config.defaultPosition;

            if (action.dock && !windowEntry.state.docked) {
                return openIntoDock(state, action.windowId, resolvedPosition);
            }

            const intermediateState = {
                ...state,
                [action.windowId]: {
                    ...windowEntry,
                    state: {
                        ...windowEntry.state,
                        open: true,
                        position: resolvedPosition,
                        zIndex: windowEntry.state.zIndex || 1000,
                    },
                },
            };

            return reorderWindows(intermediateState, action.windowId);
        }
        case ActionTypes.CloseWindow: {
            const windowEntry = state[action.windowId];

            if (!windowEntry) {
                console.warn(`Window "${action.windowId}" is not registered`);
                return state;
            }

            const wasDocked = windowEntry.state.docked;

            // Reset dock fields so a future reopen starts as floating.
            const closedEntry = {
                ...windowEntry,
                state: {
                    ...windowEntry.state,
                    open: false,
                    position: windowEntry.config.defaultPosition,
                    zIndex: windowEntry.state.zIndex,
                    minimized: false,
                    docked: false,
                    dockOrder: 0,
                    dockedHeight: undefined,
                    floatingPositionSnapshot: undefined,
                },
            };

            if (!wasDocked) {
                return { ...state, [action.windowId]: closedEntry };
            }

            // Structural op: compact sibling dockOrder + reset to equal-flex.
            const remainingDocked = getDockedIdsInOrder(state, action.windowId);
            const nextState = withRecomputedDockOrder(state, remainingDocked);
            nextState[action.windowId] = closedEntry;
            return nextState;
        }
        case ActionTypes.ToggleWindow: {
            const windowEntry = state[action.windowId];

            if (!windowEntry) {
                console.warn(`Window "${action.windowId}" is not registered`);
                return state;
            }

            const willBeOpen = !windowEntry.state.open;

            if (willBeOpen) {
                const resolvedPosition = action.position ?? windowEntry.config.defaultPosition;

                if (action.dock && !windowEntry.state.docked) {
                    return openIntoDock(state, action.windowId, resolvedPosition);
                }

                const intermediateState = {
                    ...state,
                    [action.windowId]: {
                        ...windowEntry,
                        state: {
                            ...windowEntry.state,
                            open: true,
                            position: resolvedPosition,
                        },
                    },
                };
                return reorderWindows(intermediateState, action.windowId);
            } else {
                return {
                    ...state,
                    [action.windowId]: {
                        ...windowEntry,
                        state: {
                            ...windowEntry.state,
                            open: false,
                            position: windowEntry.state.open
                                ? windowEntry.config.defaultPosition
                                : (action.position ?? windowEntry.config.defaultPosition),
                            minimized: false,
                        },
                    },
                };
            }
        }
        case ActionTypes.FocusWindow: {
            return reorderWindows(state, action.windowId);
        }
        case ActionTypes.ToggleMinimized: {
            const windowEntry = state[action.windowId];

            if (!windowEntry) {
                console.warn(`Window "${action.windowId}" is not registered`);
                return state;
            }

            return {
                ...state,
                [action.windowId]: {
                    ...windowEntry,
                    state: {
                        ...windowEntry.state,
                        minimized: !windowEntry.state.minimized,
                    },
                },
            };
        }
        case ActionTypes.DockWindow: {
            const windowEntry = state[action.windowId];

            if (!windowEntry) {
                console.warn(`Window "${action.windowId}" is not registered`);
                return state;
            }

            if (windowEntry.state.docked) {
                return state;
            }

            // Splice into the dock order (append if `atIndex` is omitted). All
            // structural ops reset sibling pinned heights to equal-flex.
            const existingDocked = getDockedIdsInOrder(state);

            const insertionIndex =
                action.atIndex == null
                    ? existingDocked.length
                    : Math.max(0, Math.min(action.atIndex, existingDocked.length));

            const newOrder = [...existingDocked];
            newOrder.splice(insertionIndex, 0, action.windowId);

            const reordered = withRecomputedDockOrder(state, newOrder);
            const target = reordered[action.windowId];
            reordered[action.windowId] = {
                ...target,
                state: {
                    ...target.state,
                    floatingPositionSnapshot: target.state.position,
                    minimized: false,
                },
            };
            return reordered;
        }
        case ActionTypes.UndockWindow: {
            const windowEntry = state[action.windowId];

            if (!windowEntry) {
                console.warn(`Window "${action.windowId}" is not registered`);
                return state;
            }

            if (!windowEntry.state.docked) {
                return state;
            }

            const restoredPosition =
                action.position ?? windowEntry.state.floatingPositionSnapshot ?? windowEntry.config.defaultPosition;

            const nextState: State = {};
            for (const [id, entry] of Object.entries(state)) {
                if (id === action.windowId) {
                    nextState[id] = {
                        ...entry,
                        state: {
                            ...entry.state,
                            docked: false,
                            dockOrder: 0,
                            dockedHeight: undefined,
                            floatingPositionSnapshot: undefined,
                            position: restoredPosition,
                        },
                    };
                } else if (entry.state.docked) {
                    nextState[id] = {
                        ...entry,
                        state: { ...entry.state, dockedHeight: undefined },
                    };
                } else {
                    nextState[id] = entry;
                }
            }
            return nextState;
        }
        case ActionTypes.SetDockedHeight: {
            const windowEntry = state[action.windowId];

            if (!windowEntry || !windowEntry.state.docked) {
                return state;
            }

            return {
                ...state,
                [action.windowId]: {
                    ...windowEntry,
                    state: { ...windowEntry.state, dockedHeight: action.height },
                },
            };
        }
        case ActionTypes.ReorderDockedWindows: {
            const nextState: State = { ...state };
            action.orderedIds.forEach((id, index) => {
                const entry = nextState[id];
                if (entry && entry.state.docked) {
                    nextState[id] = {
                        ...entry,
                        state: { ...entry.state, dockOrder: index },
                    };
                }
            });
            return nextState;
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
